import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { SchoolActivityInstance } from 'app/models';
import { AppState } from 'app/ngrx/app-state/app-state';
import { GetSessionAttendanceAction, UpdateAttendanceRecordAction } from 'app/ngrx/attendance/attendance.reducers';
import { getAttendanceForInstance, getAttendanceForInstanceAndUser, getRecord } from 'app/ngrx/attendance/attendance.state';
import * as moment from 'moment';
import { firstValueFrom, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { AttendanceRecord, AttendanceRecordState, AttendanceRecordStrState } from '../models/AttendanceRecord';
import { HttpService } from './http-service';
import { UserService } from './user.service';

// AttendanceAddRecordsReq
export type AttendanceAddRecordsReq = {
	activity_instance_id: number;
	attendance_user_ids: number[];
	state: AttendanceRecordState | string; // TODO: remove `string`
};

// AttendanceRecordUpdateReq
export type AttendanceRecordUpdateReq = {
	id: number;
	state: AttendanceRecordState | string; // TODO: remove `string`
	check_in_time?: string;
	check_out_time?: string;
	checked_out_by_id?: number;
	clear_checked_out_by?: boolean;
};

export type AttendanceRecordExportReq = {
	started_at: string;
	stopped_at: string;
};

@Injectable({
	providedIn: 'root',
})
export class AttendanceRecordService {
	constructor(private userService: UserService, private http: HttpService, private store: Store<AppState>) {}

	activityAttendance$ = (instanceId: number) => {
		return this.store.select((state) => getAttendanceForInstance(instanceId)(state.attendance));
	};

	activityAttendanceByStatus$ = (instanceId: number, status: AttendanceRecordState | AttendanceRecordStrState) => {
		return this.store
			.select((state) => getAttendanceForInstance(instanceId)(state.attendance))
			.pipe(map((records) => records.filter((record) => record.state === status)));
	};

	activityAttendanceForUser$ = (instanceId: number, userId: number) => {
		return this.store.select((state) => getAttendanceForInstanceAndUser(instanceId, userId)(state.attendance));
	};

	attendanceRecord$ = (id: number) => {
		return this.store.select((state) => getRecord(id)(state.attendance));
	};

	insertRecords(records: AttendanceRecord[]): void {
		this.store.dispatch(GetSessionAttendanceAction({ records }));
	}

	async markAllPresent(instanceId: number): Promise<void> {
		const toCheckIn = await firstValueFrom(this.activityAttendanceByStatus$(instanceId, 'not-selected'));

		for (const record of toCheckIn) {
			void this.checkIn(record.id, AttendanceRecordState.Present);
		}
	}

	attendanceTakingOpen(instanceStartTime: string): boolean {
		return moment().isAfter(moment(Date.parse(instanceStartTime)).subtract(10, 'minutes'));
	}

	activityEnded(instanceEndTime: string): boolean {
		return moment().isAfter(moment(Date.parse(instanceEndTime)));
	}

	async checkIn(id: number, state: AttendanceRecordState, instance?: SchoolActivityInstance) {
		if (!instance) {
			return;
		}

		const record = await firstValueFrom(this.attendanceRecord$(id));

		let checkInTime: string | undefined;
		if (state === AttendanceRecordState.Absent) {
			// absent records don't set check-in times automatically
			checkInTime = undefined;
		} else if (record.check_in_time) {
			// Don't override check in time if checkIn called twice
			checkInTime = undefined;
		} else if (moment().isAfter(moment(Date.parse(instance.end_time)))) {
			// if the instance has ended, we set the check-in time to the start time
			checkInTime = new Date(instance.start_time).toISOString();
		} else {
			checkInTime = new Date().toISOString();
		}

		void firstValueFrom(
			this.updateRecordState({
				id,
				state: state,
				check_in_time: checkInTime,
			})
		);
	}

	async checkOut(id: number) {
		const record = await firstValueFrom(this.attendanceRecord$(id));
		const user = await firstValueFrom(this.userService.user$);

		void firstValueFrom(
			this.updateRecordState({
				id,
				state: record.state,
				check_out_time: new Date().toISOString(),
				checked_out_by_id: user.id,
			})
		);
	}

	async checkBackIn(id: number, instance?: SchoolActivityInstance) {
		if (!instance) {
			return;
		}

		const record = await firstValueFrom(this.attendanceRecord$(id));
		const checkOutTime = new Date(instance.end_time).toISOString();

		void firstValueFrom(
			this.updateRecordState({
				id,
				state: record.state,
				check_out_time: checkOutTime,
				clear_checked_out_by: true,
			})
		);
	}

	// Since other components depend on the HTTP observable, we don't use effects
	// to dispatch our HTTP calls here. If we like this pattern going forward,
	// let's expand on it - effects can be a confusing abstraction that is often
	// not needed.

	getRecordsForInstanceId(activityInstanceId: number): Observable<AttendanceRecord[]> {
		return this.http
			.post<{ attendance_records: any[] }>('v2/attendance/activity/list', { activity_instance_id: activityInstanceId }, undefined, false)
			.pipe(
				map((resp) => {
					const records = resp.attendance_records.map((a) => AttendanceRecord.fromJSON(a)).filter((a) => a !== null) as AttendanceRecord[];
					this.store.dispatch(GetSessionAttendanceAction({ records }));
					return records;
				})
			);
	}

	addRecords(req: AttendanceAddRecordsReq): Observable<AttendanceRecord[]> {
		return this.http.post<{ attendance_records: any[] }>('v2/attendance/activity/add', req, undefined, false).pipe(
			map((resp) => {
				const records = resp.attendance_records.map((a) => AttendanceRecord.fromJSON(a)).filter((a) => a !== null) as AttendanceRecord[];
				this.store.dispatch(GetSessionAttendanceAction({ records }));
				return records;
			})
		);
	}

	updateRecordState(req: AttendanceRecordUpdateReq): Observable<AttendanceRecord | null> {
		return this.http.post<{ attendance_records: any[] }>('v2/attendance/activity/update', req, undefined, false).pipe(
			map((resp) => {
				const record = resp.attendance_records.map((a) => AttendanceRecord.fromJSON(a))[0];
				if (record) {
					this.store.dispatch(UpdateAttendanceRecordAction({ record }));
				}
				return record;
			})
		);
	}

	downloadRecords(req: AttendanceRecordExportReq): Observable<boolean> {
		return this.http.post<{ job_id: string }>('v2/attendance/activity/export', req, undefined, false).pipe(map(() => true));
	}
}
